import { useState, useEffect } from 'react';
import { ArrowLeft, Users, HelpCircle, Trophy, UserX, Check, X, Circle as CircleIcon, Square, Triangle, Diamond } from 'lucide-react';
import { getSession } from '../../services/sessionService';
import { getQuiz } from '../../services/quizService';
import { getAnswerConfig } from '../../utils/answerConfig';

const LUCIDE_BY_NAME = {
  circle: CircleIcon,
  square: Square,
  triangle: Triangle,
  diamond: Diamond,
  check: Check,
  x: X,
};

const MEDAL_COLORS = ['text-mp-fox-rust', 'text-mp-teal', 'text-mp-bg-mint'];

export default function SessionReview({ sessionId, onBack }) {
  const [session, setSession] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const sessionData = await getSession(sessionId);
        setSession(sessionData);
        if (sessionData.quizId) {
          try {
            const quizData = await getQuiz(sessionData.quizId);
            setQuiz(quizData);
          } catch {
            setQuiz(null);
          }
        }
      } catch (err) {
        setError(err.message || 'Klarte ikkje hente sesjonsdata');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-mp-bg-lightgreen font-body flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mp-magenta mx-auto mb-4" />
          <p className="font-body text-mp-stone-700">Lastar gjennomgang …</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-mp-bg-lightgreen font-body flex items-center justify-center p-4">
        <div className="bg-white border border-[rgba(163,0,97,0.2)] rounded-2xl p-6 max-w-md w-full text-center shadow-mp-1">
          <h2 className="font-display text-xl text-mp-magenta mb-2">Feil</h2>
          <p className="font-body text-mp-stone-700 mb-4">{error}</p>
          <button onClick={onBack} className="mp-cta">Tilbake</button>
        </div>
      </div>
    );
  }

  const participantsList = session?.participants ? Object.values(session.participants) : [];
  const sortedParticipants = [...participantsList].sort((a, b) => (b.score || 0) - (a.score || 0));
  const questions = quiz?.questions || [];
  const totalParticipants = participantsList.length;

  return (
    <div className="min-h-screen bg-mp-bg-lightgreen font-body text-mp-darkgreen pb-20">
      {/* Header */}
      <header className="sticky top-0 z-30 flex items-center bg-mp-bg-lightgreen/95 backdrop-blur-sm p-4 gap-3 border-b border-[rgba(17,73,66,0.10)]">
        <button
          onClick={onBack}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white text-mp-darkgreen transition-colors"
          aria-label="Tilbake"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="min-w-0">
          <h1 className="font-display text-lg text-mp-darkgreen truncate">{quiz?.title || 'Ukjend kviss'}</h1>
          <p className="font-body text-xs text-mp-stone-500">
            {new Date(session.finishedAt || session.createdAt).toLocaleDateString('nn-NO', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
            {' · '}
            {totalParticipants} deltakarar
          </p>
        </div>
      </header>

      {/* Stats */}
      <div className="px-4 py-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl border border-[rgba(17,73,66,0.10)] shadow-mp-1 p-4 text-center">
            <Users size={22} className="text-mp-teal mx-auto mb-1" />
            <p className="font-display text-2xl text-mp-darkgreen">{totalParticipants}</p>
            <p className="font-display text-[10px] text-mp-stone-500 uppercase tracking-[0.12em]">Deltakarar</p>
          </div>
          <div className="bg-white rounded-2xl border border-[rgba(17,73,66,0.10)] shadow-mp-1 p-4 text-center">
            <HelpCircle size={22} className="text-mp-magenta mx-auto mb-1" />
            <p className="font-display text-2xl text-mp-darkgreen">{questions.length}</p>
            <p className="font-display text-[10px] text-mp-stone-500 uppercase tracking-[0.12em]">Spørsmål</p>
          </div>
          <div className="bg-white rounded-2xl border border-[rgba(17,73,66,0.10)] shadow-mp-1 p-4 text-center">
            <Trophy size={22} className="text-mp-fox-rust mx-auto mb-1" />
            <p className="font-display text-base text-mp-darkgreen truncate">{sortedParticipants[0]?.name || '—'}</p>
            <p className="font-display text-[10px] text-mp-stone-500 uppercase tracking-[0.12em]">Vinnar</p>
          </div>
        </div>
      </div>

      {/* Scoreboard */}
      <div className="px-4 pb-2">
        <h3 className="font-display text-lg text-mp-darkgreen mb-3">Resultatliste</h3>
        <div className="bg-white rounded-2xl border border-[rgba(17,73,66,0.10)] shadow-mp-1 overflow-hidden">
          {sortedParticipants.length === 0 ? (
            <div className="p-8 text-center text-mp-stone-500">
              <UserX size={32} className="mx-auto mb-2" />
              <p className="font-body">Ingen deltakarar i denne sesjonen</p>
            </div>
          ) : (
            <div className="divide-y divide-[rgba(17,73,66,0.08)]">
              {sortedParticipants.map((p, i) => {
                const isTop3 = i < 3;
                return (
                  <div
                    key={p.id || i}
                    className={`flex items-center gap-3 px-4 py-3 ${isTop3 ? 'bg-mp-bg-lightgreen' : ''}`}
                  >
                    <div className="w-8 text-center shrink-0">
                      {isTop3 ? (
                        <Trophy size={20} className={MEDAL_COLORS[i]} />
                      ) : (
                        <span className="font-display text-sm text-mp-stone-500">{i + 1}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-display truncate ${isTop3 ? 'text-mp-darkgreen' : 'text-mp-stone-700'}`}>
                        {p.name}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`font-display ${isTop3 ? 'text-lg text-mp-magenta' : 'text-sm text-mp-stone-700'}`}>
                        {(p.score || 0).toLocaleString()}
                      </span>
                      <span className="font-display text-[10px] text-mp-stone-500 ml-1">pts</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Per-question breakdown */}
      {questions.length > 0 && (
        <div className="px-4 pt-6 pb-4">
          <h3 className="font-display text-lg text-mp-darkgreen mb-3">Spørsmål for spørsmål</h3>
          <div className="space-y-4">
            {questions.map((question, qIndex) => (
              <QuestionBreakdown
                key={question.id}
                question={question}
                questionIndex={qIndex}
                participants={participantsList}
                totalParticipants={totalParticipants}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function QuestionBreakdown({ question, questionIndex, participants, totalParticipants }) {
  const answerConfig = getAnswerConfig(question.type);

  const counts = new Array(question.answers.length).fill(0);
  let noAnswerCount = 0;
  participants.forEach((p) => {
    const ans = p.answers?.[question.id];
    if (ans?.answerId) {
      const idx = question.answers.findIndex((a) => a.id === ans.answerId);
      if (idx >= 0) counts[idx]++;
      else noAnswerCount++;
    } else {
      noAnswerCount++;
    }
  });

  const correctIndex = question.answers.findIndex((a) => a.isCorrect);
  const correctCount = correctIndex >= 0 ? counts[correctIndex] : 0;
  const correctPct = totalParticipants > 0 ? Math.round((correctCount / totalParticipants) * 100) : 0;

  return (
    <div className="bg-white rounded-2xl border border-[rgba(17,73,66,0.10)] shadow-mp-1 overflow-hidden">
      <div className="p-4 pb-3 border-b border-[rgba(17,73,66,0.08)]">
        <div className="flex items-start gap-3">
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-mp-bg-lightgreen text-mp-darkgreen font-display text-sm shrink-0">
            {questionIndex + 1}
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-mp-darkgreen leading-snug">{question.text}</p>
            <div className="flex items-center gap-3 mt-1.5 font-body text-xs text-mp-stone-500">
              <span>{correctPct}% svara rett</span>
              {noAnswerCount > 0 && <span>{noAnswerCount} svara ikkje</span>}
            </div>
          </div>
        </div>
      </div>

      {question.imageUrl && (
        <div className="px-4 pt-3">
          <img
            src={question.imageUrl}
            alt={`Bilete for spørsmål ${questionIndex + 1}`}
            className="w-full max-h-48 object-cover rounded-xl"
          />
        </div>
      )}

      <div className="p-4 space-y-2">
        {question.answers.map((answer, aIndex) => {
          const count = counts[aIndex];
          const pct = totalParticipants > 0 ? Math.round((count / totalParticipants) * 100) : 0;
          const isCorrect = answer.isCorrect;
          const config = answerConfig[aIndex] || answerConfig[0];
          const LucideIcon = LUCIDE_BY_NAME[config.lucideName] || CircleIcon;

          return (
            <div key={answer.id} className="flex items-center gap-2">
              {isCorrect ? (
                <Check size={18} className="text-mp-magenta shrink-0" strokeWidth={3} />
              ) : (
                <X size={18} className="text-mp-stone-300 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className={`relative h-10 rounded-xl overflow-hidden ${isCorrect ? 'bg-[rgba(163,0,97,0.06)]' : 'bg-mp-bg-lightgreen'}`}>
                  <div
                    className={`absolute inset-y-0 left-0 rounded-xl transition-all duration-500 ${
                      isCorrect ? 'bg-[rgba(163,0,97,0.18)]' : 'bg-[rgba(17,73,66,0.10)]'
                    }`}
                    style={{ width: `${Math.max(pct, 2)}%` }}
                  />
                  <div className="relative flex items-center justify-between h-full px-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <LucideIcon size={14} className={config.bgColor.replace('bg-', 'text-')} />
                      <span className={`font-display text-sm truncate ${isCorrect ? 'text-mp-magenta' : 'text-mp-darkgreen'}`}>
                        {answer.text}
                      </span>
                    </div>
                    <span className={`font-display text-xs shrink-0 ml-2 ${isCorrect ? 'text-mp-magenta' : 'text-mp-stone-500'}`}>
                      {count} ({pct}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
